import { take } from 'ramda';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  FeatureFlag,
  Organization,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { getImageSrc, getName, isOnPatientPage } from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import {
  getOrganizationInitials,
  organizationModel,
} from '@/entities/organization';
import { userModel } from '@/entities/user';

import { useLogout } from '@/features/logout';

/**
 * @deprecated
 * NOTE: This hook incapsulate too much logic and should be refactored and decomposed.
 * For example, the logic related to the user have been moved to the userModel.selectors.selectUserAvatarData.
 * On rework desktop control panel, you should use selectors and/or hooks which are using in the mobile components or useLogout hook.
 */
export const useControlPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const user = useAppSelector(userModel.selectors.selectCurrentUser);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);

  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const allOrganizations = useAppSelector(
    organizationModel.selectors.organizationSelectors.selectAll,
  );

  const isAnalyticsEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_Analytics,
    ),
  );

  const isSingleOrganizationsUser = allOrganizations?.length === 1;

  const currentOrganizationInitials = getOrganizationInitials(
    currentOrganization as Organization,
  );

  const currentOrganizationName = currentOrganization?.Name;

  const userFullName = getName(
    user?.PersonalData?.FirstName,
    user?.PersonalData?.LastName,
    {
      userLocale,
    },
  );

  const userInitials = getName(
    user?.PersonalData?.FirstName,
    user?.PersonalData?.LastName,
    {
      userLocale,
      shortFirstName: true,
      shortLastName: true,
    },
  );

  const userAvatarAssetSrc = getImageSrc(user?.AvatarAssetID, 'thumbnail');

  const profileFullInitials = currentOrganization
    ? currentOrganizationInitials
    : userInitials;

  const profileInitials = take(2, profileFullInitials);

  const profileName = currentOrganization
    ? currentOrganizationName
    : userFullName;

  const profileAssetSrc = currentOrganization
    ? getImageSrc(currentOrganization?.LogoAssetID ?? '', 'thumbnail')
    : userAvatarAssetSrc;

  const profileAvatarSize = isSingleOrganizationsUser ? 44 : 40;

  const organizationChangeHandle = (organizationID: string) => {
    dispatch(
      organizationModel.thunks.setCurrentOrganizationByID(organizationID),
    );

    if (isOnPatientPage(location.pathname)) {
      navigate(PATHS.patients);
    }
  };

  const logoutHandle = useLogout();

  const openGetFullAccessModalHandle = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.GetFullAccessModal,
      }),
    );
  }, []);

  const navigateToCompanySettingsHandle = () =>
    navigate({
      pathname: PATHS.companySettings,
      search: 'tab=CompanyInfo',
    });

  return {
    allOrganizations,
    profileAssetSrc,
    profileInitials,
    profileName,
    profileAvatarSize,
    currentOrganization,
    currentOrganizationInitials,
    currentOrganizationName,
    isSingleOrganizationsUser,
    userAvatarAssetSrc,
    userFullName,
    userInitials,
    isAnalyticsEnabled,
    openGetFullAccessModalHandle,
    logoutHandle,
    navigateToCompanySettingsHandle,
    organizationChangeHandle,
  };
};
